import { gql } from "@apollo/client";
import FormSection from "controls/FormSection";
import Message from "controls/Message";
import MultiValuesField from "controls/MultiValuesField";
import getUiString from "helpers/getUiString";
import sameOrNull from "helpers/sameOrNull";
import toCapitalizedWords from "helpers/toCapitalizedWords";
import useData from "hooks/useData";
import DurationInput from "inputs/DurationInput";
import NumberInput from "inputs/NumberInput";
import SelectInput from "inputs/SelectInput";
import SwitchInput from "inputs/SwitchInput";
import TextInput from "inputs/TextInput";
import React, { useState } from "react";

const SPORT_BASKETBALL = "basketball";
const SPORT_MINIBALL = "miniball";
const SPORT_NETBALL = "netball";

export const ScorekeeperConfigsFormFragment = gql`
  fragment ScorekeeperConfigsFormFragment on ScorekeeperConfig {
    sport

    # clock
    periodType
    periodLength
    overtimePeriodLength
    gameClockVisible
    gameClockStartMs

    # paticipants
    scoringMode
    allowNewTeamMembers
    numberOfStartingPlayers
    tapInPlayersByDefault

    # scoring
    allowTwoPointShots
    allowThreePointShots
    allowMisses
    foulsPerPeriod
    maxPlayerFouls
    pointsForForfeit

    # fiba
    fibaScoring
    fibaScoresheetLayoutHideProtestSection
    fibaScoresheetRecipientEmails
  }
`;

const EXPECTED_FIBA_VALUES = {
  periodType: {
    value: "quarters",
    description: "Period type should be quarters",
  },
  periodLength: {
    value: 10,
    description: "Period length should be 10 minutes",
  },
  overtimePeriodLength: {
    value: 5,
    description: "Overtime period length should be 5 minutes",
  },
  foulsPerPeriod: {
    value: 4,
    description: "Fouls per period should be 4",
  },
  maxPlayerFouls: {
    value: 5,
    description: "Max player fouls should be 5",
  },
  numberOfStartingPlayers: {
    value: 5,
    description: "Number of starting players should be 5",
  },
};

export default function ScorekeeperConfigsForm({ scorekeeperConfigs }) {
  // const [gameC
  const [gameClockVisibles, gameClockVisiblesSet] = useState(scorekeeperConfigs.map((v) => v.gameClockVisible));
  const [fibaScorings, fibaScoringsSet] = useState(scorekeeperConfigs.map((v) => v.fibaScoring));
  const [periodTypes, periodTypesSet] = useState(scorekeeperConfigs.map((v) => v.periodType));
  const [periodLengths, periodLengthsSet] = useState(scorekeeperConfigs.map((v) => v.periodLength));
  const [overtimePeriodLengths, overtimePeriodLengthsSet] = useState(
    scorekeeperConfigs.map((v) => v.overtimePeriodLength),
  );
  const [maxPlayerFoulses, maxPlayerFoulsesSet] = useState(scorekeeperConfigs.map((v) => v.maxPlayerFouls));
  const [foulsPerPeriods, foulsPerPeriodsSet] = useState(scorekeeperConfigs.map((v) => v.foulsPerPeriod));
  const [pointsForForfeits, pointsForForfeitsSet] = useState(scorekeeperConfigs.map((v) => v.pointsForForfeit));
  const [numberOfStartingPlayerses, numberOfStartingPlayersesSet] = useState(
    scorekeeperConfigs.map((v) => v.numberOfStartingPlayers),
  );

  const fibaValues = {
    periodType: sameOrNull(periodTypes),
    periodLength: sameOrNull(periodLengths),
    overtimePeriodLength: sameOrNull(overtimePeriodLengths),
    maxPlayerFouls: sameOrNull(maxPlayerFoulses),
    foulsPerPeriod: sameOrNull(foulsPerPeriods),
    numberOfStartingPlayers: sameOrNull(numberOfStartingPlayerses),
  };

  const [data] = useData(gql`
    query ScorekeeperConfigsForm {
      periodTypes
      scorekeeperScoringModes
    }
  `);

  return (
    <>
      <MultiValuesField
        name="periodType"
        label="Period Type"
        values={periodTypes}
        onChange={periodTypesSet}
        required
        input={<SelectInput options={data?.periodTypes.map((v) => [toCapitalizedWords(v), v])} />}
        helperTextUiStringKey="readmin.scorekeeper_config.fields.period_type"
      />
      <MultiValuesField
        name="periodLength"
        label="Period Length"
        defaultValues={periodLengths}
        onChange={periodLengthsSet}
        required
        input={<NumberInput unit="minutes" />}
        helperTextUiStringKey="readmin.scorekeeper_config.fields.period_length"
      />
      <MultiValuesField
        name="overtimePeriodLength"
        label="Overtime Period Length"
        values={overtimePeriodLengths}
        onChange={overtimePeriodLengthsSet}
        required
        input={<NumberInput unit="minutes" />}
        helperTextUiStringKey="readmin.scorekeeper_config.fields.overtime_period_length"
      />
      <MultiValuesField
        name="pointsForForfeit"
        label="Points for Forfeit"
        values={pointsForForfeits}
        onChange={pointsForForfeitsSet}
        input={<NumberInput />}
        helperTextUiStringKey="readmin.scorekeeper_config.fields.points_for_forfeit"
      />
      <MultiValuesField
        name="gameClockVisible"
        label="Game Clock Visible"
        values={gameClockVisibles}
        onChange={gameClockVisiblesSet}
        input={<SwitchInput />}
        helperTextUiStringKey="readmin.scorekeeper_config.fields.game_clock_visible"
      />
      {sameOrNull(gameClockVisibles) && (
        <MultiValuesField
          name="gameClockStartMs"
          label="Game Clock Start"
          defaultValues={scorekeeperConfigs.map((v) => v.gameClockStartMs)}
          required
          input={<DurationInput unit="milliseconds" />}
          helperTextUiStringKey="readmin.scorekeeper_config.fields.game_clock_start"
        />
      )}
      <FormSection header="Participant Settings">
        <MultiValuesField
          name="scoringMode"
          label="Scoring Mode"
          defaultValues={scorekeeperConfigs.map((v) => v.scoringMode)}
          disabled={scorekeeperConfigs.some((c) => c.sport === SPORT_MINIBALL)}
          required
          input={
            <SelectInput
              options={data?.scorekeeperScoringModes.map((scoringMode) => [
                getUiString(`readmin.scorekeeper_config.fields.scoring_mode_text.${scoringMode}`),
                scoringMode,
                {
                  description: getUiString(`readmin.scorekeeper_config.fields.scoring_mode_description.${scoringMode}`),
                },
              ])}
            />
          }
          helperTextUiStringKey="readmin.scorekeeper_config.fields.scoring_mode"
        />
        <MultiValuesField
          name="numberOfStartingPlayers"
          label="Number of starting players"
          values={numberOfStartingPlayerses}
          onChange={numberOfStartingPlayersesSet}
          required
          input={<NumberInput />}
          helperTextUiStringKey="readmin.scorekeeper_config.fields.number_of_starting_players"
        />
        <MultiValuesField
          name="allowNewTeamMembers"
          label="Allow new team members to be added on Scorekeeper"
          defaultValues={scorekeeperConfigs.map((v) => v.allowNewTeamMembers)}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.scorekeeper_config.fields.allow_new_team_members"
        />
        <MultiValuesField
          name="tapInPlayersByDefault"
          label="Tap in players by default"
          defaultValues={scorekeeperConfigs.map((v) => v.tapInPlayersByDefault)}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.scorekeeper_config.fields.tap_in_players_by_default"
        />
      </FormSection>
      <FormSection header="Scoring Settings">
        {scorekeeperConfigs.every((v) => [SPORT_BASKETBALL, SPORT_MINIBALL].includes(v.sport)) && (
          <MultiValuesField
            name="allowThreePointShots"
            label="Allow 3pt shots to be scored"
            defaultValues={scorekeeperConfigs.map((v) => v.allowThreePointShots)}
            input={<SwitchInput />}
            helperTextUiStringKey="readmin.scorekeeper_config.fields.allow_three_point_shots"
          />
        )}
        {scorekeeperConfigs.every((v) => [SPORT_NETBALL].includes(v.sport)) && (
          <MultiValuesField
            name="allowTwoPointShots"
            label="Allow 2pt shots to be scored"
            defaultValues={scorekeeperConfigs.map((v) => v.allowTwoPointShots)}
            input={<SwitchInput />}
            helperTextUiStringKey="readmin.scorekeeper_config.fields.allow_two_point_shots"
          />
        )}
        <MultiValuesField
          name="allowMisses"
          label="Allow misses to be scored"
          defaultValues={scorekeeperConfigs.map((v) => v.allowMisses)}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.scorekeeper_config.fields.allow_misses"
        />
        <MultiValuesField
          name="foulsPerPeriod"
          label="Team fouls per period before bonus"
          values={foulsPerPeriods}
          onChange={foulsPerPeriodsSet}
          required
          input={<NumberInput />}
          helperTextUiStringKey="readmin.scorekeeper_config.fields.fouls_per_period"
        />
        <MultiValuesField
          name="maxPlayerFouls"
          label="Max player fouls in game before they must leave the game"
          values={maxPlayerFoulses}
          onChange={maxPlayerFoulsesSet}
          input={<NumberInput />}
          helperTextUiStringKey="readmin.scorekeeper_config.fields.max_player_fouls"
        />
      </FormSection>
      {scorekeeperConfigs.every((v) => SPORT_BASKETBALL === v.sport) && (
        <FormSection
          header="FIBA Scoring Mode"
          defaultExpanded={!!sameOrNull(scorekeeperConfigs.map((v) => v.fibaScoring))}
        >
          {sameOrNull(fibaScorings) &&
            Object.entries(EXPECTED_FIBA_VALUES).map(
              ([key, { value, description }]) =>
                fibaValues[key] !== value && (
                  <Message type="warning" key={key} content={`${description} for FIBA games`} />
                ),
            )}

          <MultiValuesField
            name="fibaScoring"
            label="Use FIBA Scoring mode"
            values={fibaScorings}
            onChange={fibaScoringsSet}
            input={<SwitchInput />}
            helperTextUiStringKey="readmin.scorekeeper_config.fields.fiba_scoring"
          />
          {sameOrNull(fibaScorings) && (
            <>
              <MultiValuesField
                name="fibaScoresheetLayoutHideProtestSection"
                label="FIBA scoresheet layout: hide bottom left protest section"
                defaultValues={scorekeeperConfigs.map((v) => v.fibaScoresheetLayoutHideProtestSection)}
                input={<SwitchInput />}
                helperTextUiStringKey="readmin.scorekeeper_config.fields.fiba_scoresheet_layout_hide_protest_section"
              />
              <MultiValuesField
                name="fibaScoresheetRecipientEmails"
                label="FIBA Scoresheet PDF Recipient Emails"
                defaultValues={scorekeeperConfigs.map((v) => v.fibaScoresheetRecipientEmails)}
                input={<TextInput />}
                helperTextUiStringKey="readmin.scorekeeper_config.fields.fiba_scoresheet_recipient_emails"
              />
            </>
          )}
        </FormSection>
      )}
    </>
  );
}
